/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2025 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { BodyPartFieldProps } from "../../../types/rule";
import { getSelectStyles, IN, NOT_IN, BODY_PART } from "../../../constant";
import { OptionTypeBase } from "../../../types/common";

const BodyPart: FC<BodyPartFieldProps> = ({
  disabled,
  id,
  parent,
  errors,
  bodyPart,
  operator,
  onValueChangeHandler,
  isDark,
  options,
}: BodyPartFieldProps) => {
  const { t } = useTranslation("rule");
  const [selectedOptions, setSelectedOptions] = useState(null);
  const [selectedOperator, setSelectedOperator] = useState(null);

  useEffect(() => {
    if (options?.length > 0) {
      setSelectedOptions(
        options.filter((item) => bodyPart?.includes(item.value))
      );
    }
  }, [options, bodyPart]);

  useEffect(() => {
    setSelectedOperator(operator);
  }, [operator]);

  const dispatchNewValue = (data: string[], newOperator: string) => {
    onValueChangeHandler(
      {
        scope: "conditions",
        id,
        type: BODY_PART,
        stringCollectionProperty: {
          property: "bodyPart",
          operator: newOperator,
          values: data,
        },
      },
      parent
    );
  };

  const selectOperator = (update: any) => {
    if ([IN, NOT_IN].includes(update?.value)) {
      dispatchNewValue(
        selectedOptions?.map((item) => item.value),
        update?.value
      );
    }
  };

  const operatorOptions = useMemo(() => {
    const result: Array<OptionTypeBase> = [
      {
        label: `${t("in_")}`,
        value: IN,
      },
      {
        label: `${t("not_in")}`,
        value: NOT_IN,
      },
    ];
    return result;
  }, [t]);

  const hasError = () => {
    return (
      errors?.find((item) => item.id === id && item.type === BODY_PART) !==
      undefined
    );
  };

  return (
    <div className="row">
      <div className="mb-1 col col-md-3">
        <Select
          styles={getSelectStyles(isDark)}
          menuPlacement="auto"
          menuPosition="fixed"
          isDisabled={disabled}
          id={`${id}_operator`}
          name="operator"
          classNamePrefix="form-control-select"
          options={operatorOptions}
          placeholder={t("select_operator")}
          onChange={selectOperator}
          value={operatorOptions?.find((option) => {
            return option?.value === selectedOperator;
          })}
        />
        {hasError() && !selectedOperator && (
          <label
            className="validation-invalid-label"
            htmlFor={`${id}_operator`}
          >
            {t("field_required")}
          </label>
        )}
      </div>
      <div className="col-md-4 col-xs-12 mb-1">
        <Select
          styles={getSelectStyles(isDark)}
          menuPlacement="auto"
          menuPosition="fixed"
          isDisabled={disabled}
          id={`${id}_operator`}
          name="operator"
          classNamePrefix="form-control-select"
          options={options}
          placeholder={t("select")}
          isMulti
          onChange={(selectedItems) => {
            dispatchNewValue(
              selectedItems.map((item) => item.value),
              operator
            );
          }}
          value={selectedOptions}
        />
        {hasError() && (!selectedOptions || selectedOptions?.length == 0) && (
          <label
            className="validation-invalid-label"
            htmlFor={`${id}_body_part_field`}
          >
            {t("form:hl7.field_required")}
          </label>
        )}
      </div>
    </div>
  );
};

export default BodyPart;
